import { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import api from '../utils/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [books, setBooks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Load user from API on mount if token exists
    useEffect(() => {
        const loadUser = async () => {
            const savedUser = Cookies.get('islam101_user');
            if (savedUser) {
                const parsedUser = JSON.parse(savedUser);
                if (parsedUser.token) {
                    try {
                        const { data } = await api.get('/auth/profile');

                        setUser({
                            ...data,
                            token: parsedUser.token,
                            categories: []
                        });

                        await fetchCategories();
                        await fetchBooks()
                    } catch (error) {
                        console.error('Session expired', error);
                        logout();
                    }
                }
            }
            setIsLoading(false);
        };

        loadUser();
    }, []);

    const persistUser = (updatedUser) => {
        // We only store essential auth data in cookie, data is in DB
        const cookieData = {
            id: updatedUser.id,
            name: updatedUser.name,
            email: updatedUser.email,
            role: updatedUser.role,
            token: updatedUser.token
        };
        Cookies.set('islam101_user', JSON.stringify(cookieData), { expires: 30 });
        setUser(updatedUser);
    };

    const login = async (credentials) => {
        try {
            const { data } = await api.post('/auth/login', credentials);

            const profileRes = await api.get('/auth/profile', {
                headers: { Authorization: `Bearer ${data.token}` }
            });

            const fullUser = {
                ...profileRes.data,
                token: data.token,
                categories: [] // initialize
            };

            persistUser(fullUser);

            // ✅ fetch AFTER user is set
            await fetchCategories();

            return true;
        } catch (error) {
            toast.error(error.response?.data?.message || 'Login failed');
            throw error;
        }
    };

    const register = async (userData) => {
        try {
            await api.post('/auth/register', userData);
            // Do NOT fetch profile or persist user
            return true;
        } catch (error) {
            toast.error(error.response?.data?.message || 'Registration failed');
            throw error;
        }
    };

    const fetchCategories = async () => {
        const res = await api.get('/categories');

        setUser(prev => ({
            ...prev,
            categories: res.data
        }));
    };

    const logout = () => {
        setUser(null);
        Cookies.remove('islam101_user');
    };

    // Generic updater for local state + optimistic UI, then syncs to background
    const updateUser = async (updates) => {
        // Optimistic update
        setUser((prev) => ({ ...prev, ...updates }));

        // Sync preferences to DB
        if (updates.preferences) {
            try {
                await api.put('/auth/profile', { preferences: updates.preferences });
            } catch (error) {
                console.error('Failed to sync preferences', error);
            }
        }
    };

    const fetchNotes = async () => {
        try {
            const res = await api.get('/user/notes');

            setUser((prev) => ({
                ...prev,
                progress: {
                    ...(prev?.progress || {}),
                    notes: res.data
                }
            }));
        } catch (err) {
            console.error('❌ fetchNotes failed', err);
        }
    };

    const addNote = async (note) => {
        try {
            await api.post('/user/notes', {
                referenceType: note.type,
                reference: note.reference,
                text: note.content
            });

            // Re-fetch from server to ensure consistency
            await fetchNotes();

        } catch (error) {
            console.error("Failed to save note", error);
            toast.error("Failed to save note");

            // Optional: rollback optimistic update
            fetchNotes();
        }
    };

    const addFavorite = async (favorite) => {
        // Optimistic
        setUser((prev) => {
            const exists = prev?.progress?.favorites?.some(f => f.id === favorite.id);
            if (exists) return prev;
            const updatedFavorites = [...(prev?.progress?.favorites || []), favorite];
            return { ...prev, progress: { ...prev.progress, favorites: updatedFavorites } };
        });

        try {
            await api.post('/user/progress', {
                category: favorite.type, // Ensure favorite object has 'type' (quran, hadith, etc)
                itemId: favorite.id,
                action: 'favorite'
            });
        } catch (error) {
            console.error("Failed to save favorite", error);
        }
    };

    const updateQuranProgress = async (type, surahNumber) => {
        // Optimistic
        setUser((prev) => {
            const currentProgress = prev?.progress?.quran || { clicked: [], read: [], listened: [] };
            const list = currentProgress[type] || [];
            if (!list.includes(surahNumber)) {
                const updatedList = [...list, surahNumber];
                return {
                    ...prev,
                    progress: {
                        ...prev.progress,
                        quran: { ...currentProgress, [type]: updatedList }
                    }
                };
            }
            return prev;
        });

        try {
            await api.post('/user/progress', {
                category: 'quran',
                itemId: surahNumber,
                action: type // 'clicked', 'read', 'listened'
            });
        } catch (error) {
            console.error("Failed to sync quran progress");
            console.log(error);
            
        }
    };

    const updateHadithProgress = async (type, hadithId) => {
        setUser((prev) => {
            const currentProgress = prev?.progress?.hadith || { completed: [], favorites: [] };
            const list = currentProgress[type] || [];
            if (!list.includes(hadithId)) {
                const updatedList = [...list, hadithId];
                return {
                    ...prev,
                    progress: {
                        ...prev.progress,
                        hadith: { ...currentProgress, [type]: updatedList }
                    }
                };
            }
            return prev;
        });

        try {
            await api.post('/user/progress', {
                category: 'hadith',
                itemId: hadithId,
                action: type
            });
        } catch (error) {
            console.error("Failed to sync hadith progress");
            console.log(error);
            
        }
    };

    const updateDuaProgress = async (type, duaId) => {
        setUser((prev) => {
            const currentProgress = prev?.progress?.duas || { completed: [], favorites: [] };
            const list = currentProgress[type] || [];
            if (!list.includes(duaId)) {
                const updatedList = [...list, duaId];
                return {
                    ...prev,
                    progress: {
                        ...prev.progress,
                        duas: { ...currentProgress, [type]: updatedList }
                    }
                };
            }
            return prev;
        });

        try {
            await api.post('/user/progress', {
                category: 'dua',
                itemId: duaId,
                action: type
            });
        } catch (error) {
            console.error("Failed to sync dua progress");
            console.log(error);
            
        }
    };

    const updateBookProgress = async (type, bookId) => {
        setUser((prev) => {
            const currentProgress = prev?.progress?.books || { read: [], favorites: [] };
            const list = currentProgress[type] || [];
            if (!list.includes(bookId)) {
                const updatedList = [...list, bookId];
                return {
                    ...prev,
                    progress: {
                        ...prev.progress,
                        books: { ...currentProgress, [type]: updatedList }
                    }
                };
            }
            return prev;
        });

        try {
            await api.post('/user/progress', {
                category: 'book',
                itemId: bookId,
                action: type
            });
        } catch (error) {
            console.error("Failed to sync book progress");
            console.log(error);
            
        }
    };

    // CUSTOM CONTENT WRAPPERS
    const createPlaylist = async (name) => {
        const newPlaylist = { id: `local-${Date.now()}`, name: name.substring(0, 15), surahs: [] };
        setUser(prev => ({ ...prev, playlists: [...(prev.playlists || []), newPlaylist] }));

        try {
            await api.post('/user/custom', { type: 'playlist', action: 'add', content: newPlaylist });
            // Update ID with real DB ID if needed, but for now we might rely on reload or careful ID management
        } catch (err) { console.error(err); }
    };

    const addToPlaylist = async (playlistId, surahNumber) => {
        // Need to find the playlist and update it
        let updatedContent = null;
        setUser(prev => {
            const updatedPlaylists = prev.playlists.map(p => {
                if (p.id === playlistId && !p.surahs.includes(surahNumber)) {
                    updatedContent = { ...p, surahs: [...p.surahs, surahNumber] };
                    return updatedContent;
                }
                return p;
            });
            return { ...prev, playlists: updatedPlaylists };
        });

        if (updatedContent) {
            try {
                await api.post('/user/custom', { type: 'playlist', action: 'update', content: updatedContent });
            } catch (err) { console.error(err); }
        }
    };

    // Implementation for other custom content follows similar pattern:
    // 1. Optimistic UI update
    // 2. API call to /user/custom with action 'add', 'update', or 'delete'

    const addCustomDua = async (dua) => {
        const newDua = { ...dua, id: `custom-${Date.now()}`, isCustom: true };
        setUser(prev => ({ ...prev, customDuas: [...(prev.customDuas || []), newDua] }));
        try {
            await api.post('/user/custom', { type: 'custom_dua', action: 'add', content: newDua });
        } catch (err) { console.error(err); }
    };

    const deleteCustomDua = async (duaId) => {
        setUser(prev => ({ ...prev, customDuas: prev.customDuas.filter(d => d.id !== duaId) }));
        try {
            await api.post('/user/custom', { type: 'custom_dua', action: 'delete', id: duaId });
        } catch (err) { console.error(err); }
    };

    const addCategory = async (name) => {
        const res = await api.post('/categories', { name });

        await fetchCategories();

        return res;
    };

    const addItemToCategory = async (categoryId, item) => {
        await api.post('/categories/items', {
            category_id: categoryId,
            item_type: item.type,
            item_id: item.id,
            reference: item.ref,
            content: item.content
        });

        await fetchCategories();

        toast.success('Saved to category');
    };

    const deleteCategory = async (categoryId) => {
        await api.delete(`/categories/${categoryId}`);

        setUser(prev => ({
            ...prev,
            categories: prev.categories.filter(cat => cat.id !== categoryId)
        }));

        toast.success('Category deleted');
    };

    const removeItemFromCategory = async (categoryId, itemId) => {
        await api.delete(`/categories/${categoryId}/items/${itemId}`);

        setUser(prev => ({
            ...prev,
            categories: prev.categories.map(cat =>
                cat.id === categoryId
                    ? {
                        ...cat,
                        items: cat.items.filter(item => item.id !== itemId)
                    }
                    : cat
            )
        }));

        toast.success('Item removed');
    };

    // ... (Other functions like addCategory, deleteCategory, etc. would follow this pattern)
    // For brevity, I'm including the essential structure. You should migrate all local state modifiers to this pattern.

    const fetchBooks = async () => {

        try {
            const res = await api.get('/books');
            setBooks(res.data);
        } catch (err) {
            console.error('❌ fetchBooks failed', err);
        }
    };

    const addCustomBook = async (book) => {
        try {
            await api.post('/books', book, { withCredentials: true });
            await fetchBooks();
        } catch (err) {
            toast.error('Failed to add book');
            console.log(err);
            
        }
    };

    const updateCustomBook = async (bookId, book) => {

        // console.log("bookID: ", bookId);
        // console.log("Book: ", book);
        
        try {
            const res = await api.put(`/books/${bookId}`, book, { withCredentials: true });
            setUser(prev => ({
                ...prev,
                customBooks: prev.customBooks.map(b => b.id === bookId ? res.data.book : b)
            }));
            toast.success('Book updated');
        } catch (err) {
            toast.error('Failed to update book');
            console.log(err);
        }
    };

    const deleteBook = async (bookId) => {
        try {
            await api.delete(`/books/${bookId}`, { withCredentials: true });
            await fetchBooks();
        } catch (err) {
            toast.error('Failed to delete book');
            console.log(err);
        }
    };

    const value = {
        user,
        isLoading,
        login,
        register,
        logout,
        updateUser,
        addNote,
        addFavorite,
        updateQuranProgress,
        updateHadithProgress,
        updateDuaProgress,
        updateBookProgress,
        createPlaylist,
        addToPlaylist,
        addCustomDua,
        deleteCustomDua,
        // Add other functions mapped to API calls...
        addCategory,
        addItemToCategory,
        deleteCategory,
        removeItemFromCategory,
        fetchBooks,
        addCustomBook,
        updateCustomBook,
        deleteBook,
        books,
        setBooks,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin'
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};